'use client';

import { useState } from 'react';

import { Text } from '@northladder/i18n';
import { ErrorEventsEnum, TTextLocalized } from '@northladder/utilities';
import { logErrorToSentry } from '@northladder/web-utils';

import { translations } from '../../../translations';

import { CameraLoader } from '../../image-cropper';
import { CameraProgressLoader } from '../../loaders';

import { useSimpleCameraCheck } from './utils';

interface ISimpleAutoCameraProps {
  /**
   * Error message when camera access is denied
   */
  cameraAccessErrorMessage?: TTextLocalized;
  className?: string;
  classNameError?: string;
  facingMode?: 'front' | 'back';
  /**
   * The camera height, default to `932`
   */
  height?: number;
  /**
   * Whether to render the camera view mirrored or not, default to `true`
   */
  mirrored?: boolean;
  onCaptureSuccess: () => void;
  /**
   * The camera with, default to `430`
   */
  width?: number;
}

/**
 * -----------------------------------------------------------------------------
 * This renders a simple camera element that loads a device camera.
 *
 * It uses pixel analysis to check if the camera is working and showing content,
 * then triggers a success callback.
 */
export function SimpleAutoCamera({
  cameraAccessErrorMessage,
  className = '',
  classNameError = '',
  facingMode = 'front',
  height = 932,
  mirrored = true,
  onCaptureSuccess,
  width = 430,
}: ISimpleAutoCameraProps) {
  const [isCameraLoading, setIsCameraLoading] = useState(true);

  const { isDetecting, hasAccessError, warningMessage, Webcam, webcamRef } =
    useSimpleCameraCheck({ onCaptureSuccess });

  /**
   * Handle camera error event.
   */
  const handleCameraError = (error: unknown) => {
    logErrorToSentry({
      error,
      errorName: 'SIMPLE AUTO CAMERA / FRONT CAMERA',
      source: 'SimpleAutoCamera.handleCameraError',
      errorKey: ErrorEventsEnum.ERROR_IN_ATTACHMENT,
    });
  };

  // Determine error message display CSS class
  const errorClass = hasAccessError
    ? 'opacity-100 z-20'
    : 'opacity-0 z-10 pointer-events-none';

  // Determine camera facing mode
  const cameraFacingMode =
    facingMode === 'front' ? 'user' : { exact: 'environment' };

  /**
   * Callback function invoked when the camera is successfully loaded.
   */
  const handleCameraLoaded = () => {
    setIsCameraLoading(false);
  };

  return (
    <div className="relative h-full w-full overflow-hidden rounded-xl border-2 border-gray-300">
      {isCameraLoading ? <CameraLoader className="bg-black" /> : null}

      {hasAccessError ? (
        <button
          className={`
            absolute left-0 right-0 top-0 z-20 flex h-full w-full flex-col items-center
            justify-center bg-red-100
            ${classNameError}
            ${errorClass}
          `}
          onClick={() => window.location.reload()}
          type="button"
        >
          <p className="w-10/12 text-center text-sm text-red-800">
            <Text
              text={
                cameraAccessErrorMessage ||
                translations.camera.cameraAccessErrorMessage
              }
            />
          </p>
        </button>
      ) : null}

      {warningMessage ? (
        <div
          className="
            absolute top-12 left-0 z-[100] w-full bg-black/80 p-4 text-center
            opacity-90 border-border/20 border
          "
        >
          <p className="text-sm text-red-400">
            {/* TODO: Add translation for this later and get it from the warningMessage */}
            There is not enough visible content being displayed on the camera.
          </p>
        </div>
      ) : null}

      <Webcam
        ref={webcamRef}
        audio={false}
        className={`z-50 rounded-xl bg-white object-cover ${className}`}
        height={height}
        minScreenshotHeight={height}
        minScreenshotWidth={width}
        mirrored={mirrored}
        muted
        onError={handleCameraError}
        onErrorCapture={handleCameraError}
        onUserMedia={handleCameraLoaded}
        screenshotFormat="image/jpeg"
        screenshotQuality={0.65}
        videoConstraints={{
          width,
          height,
          facingMode: cameraFacingMode,
        }}
        width={width}
      />

      {isDetecting ? <CameraProgressLoader className="mx-auto" /> : null}
    </div>
  );
}
